<template>
  <div
    v-if="isOpen"
    ref="modalRef"
    class="settings-modal"
    :style="modal.modalStyle.value"
    @mousedown="bringToFront"
  >
    <!-- Header (draggable) -->
    <div
      ref="headerRef"
      class="modal-header"
      :class="{ flashing: modal.isFlashing.value }"
      @mousedown="onStartDrag"
    >
      <span class="modal-title">Settings</span>
      <button class="close-btn" @click="close" title="Close"><XMarkIcon class="close-icon" /></button>
    </div>

    <!-- Content -->
    <div class="modal-content">
      <!-- All settings in a single list -->
      <div class="setting-row">
        <span class="setting-label">Theme</span>
        <div class="toggle-container theme-toggle">
          <span :class="{ active: currentTheme === 'light' }" @click="setTheme('light')">Light</span>
          <span :class="{ active: currentTheme === 'dark' }" @click="setTheme('dark')">Dark</span>
        </div>
      </div>
      <div class="setting-row">
        <span class="setting-label">Chart Mode</span>
        <div class="toggle-container" @click="toggleChartMode">
          <span :class="{ active: chartMode === 'gas' }">Gas</span>
          <span :class="{ active: chartMode === 'txCount' }">TX Count</span>
        </div>
      </div>
      <div class="setting-row">
        <span class="setting-label">
          Sparklines
          <span class="beta-badge">BETA</span>
        </span>
        <div class="toggle-container" @click="toggleSparklines">
          <span :class="{ active: !showSparklines }">Off</span>
          <span :class="{ active: showSparklines }">On</span>
        </div>
      </div>

      <!-- Dev Settings (only shown in dev mode) -->
      <div v-if="isDev" class="setting-row">
        <span class="setting-label">TX Multiplier</span>
        <div class="multiplier-control">
          <input
            type="range"
            min="1"
            max="1000"
            step="1"
            :value="txMultiplier"
            @input="updateTxMultiplier"
          />
          <span class="multiplier-value">{{ txMultiplier }}×</span>
        </div>
      </div>

      <!-- Hidden Circles -->
      <div v-if="deletedCircles.length > 0" class="hidden-circles-section">
        <div class="section-title">HIDDEN CIRCLES</div>
        <div class="hidden-circles-list">
          <div
            v-for="circle in deletedCircles"
            :key="circle.address"
            class="hidden-circle-item"
          >
            <div class="circle-info">
              <span class="circle-name">{{ circle.projectName || formatAddr(circle.address) }}</span>
              <span v-if="circle.projectName" class="circle-address">{{ formatAddr(circle.address) }}</span>
            </div>
            <button class="restore-btn" @click="restoreCircle(circle.address)" title="Restore circle">
              <ArrowPathIcon class="restore-icon" />
            </button>
          </div>
        </div>
        <button v-if="deletedCircles.length > 1" class="restore-all-btn" @click="restoreAllCircles">
          Restore All
        </button>
      </div>
    </div>

    <!-- Resize Handles -->
    <div class="resize-handle resize-right" @mousedown.stop="(e) => modal.startResize('right', e)"></div>
    <div class="resize-handle resize-bottom" @mousedown.stop="(e) => modal.startResize('bottom', e)"></div>
    <div class="resize-handle resize-corner" @mousedown.stop="(e) => modal.startResize('corner', e)"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { XMarkIcon, ArrowPathIcon } from '@/components/icons';
import { userSettings, type DeletedCircleInfo } from '@/stores/userSettings';
import type { ThemeMode } from '@/config/themes';
import { useModalDragResize } from '@/composables/useModalDragResize';
import { formatAddress } from '@/utils/formatters';

// Props
export interface SettingsModalProps {
  zIndexBase?: number;
  chartMode: 'gas' | 'txCount';
  txMultiplier: number;
  isDev: boolean;
}

const props = withDefaults(defineProps<SettingsModalProps>(), {
  zIndexBase: 1000,
  isDev: false
});

// Emits
const emit = defineEmits<{
  close: [];
  focus: [];
  'update:chartMode': [mode: 'gas' | 'txCount'];
  'update:txMultiplier': [value: number];
  'restoreCircle': [address: string];
}>();

// Modal drag/resize composable
const modal = useModalDragResize({
  initialWidth: 340,
  initialHeight: 320,
  minWidth: 280,
  minHeight: 150,
  zIndexBase: props.zIndexBase
});

// State
const isOpen = ref(false);
const headerRef = ref<HTMLElement>();

// Theme
const currentTheme = ref<ThemeMode>(userSettings.theme);
let unsubscribeSettings: (() => void) | null = null;

const setTheme = (mode: ThemeMode) => {
  userSettings.theme = mode;
  currentTheme.value = mode;
};

// Sparklines (beta feature)
const showSparklines = ref(userSettings.showSparklines);

const toggleSparklines = () => {
  showSparklines.value = !showSparklines.value;
  userSettings.showSparklines = showSparklines.value;
};

// Deleted circles
const deletedCircles = ref<DeletedCircleInfo[]>([]);

const refreshDeletedCircles = () => {
  deletedCircles.value = userSettings.getDeletedCirclesList();
};

const restoreCircle = (address: string) => {
  userSettings.restoreCircle(address);
  refreshDeletedCircles();
  emit('restoreCircle', address);
};

const restoreAllCircles = () => {
  userSettings.restoreAllCircles();
  refreshDeletedCircles();
};

// Use shared formatter with custom params for addresses
const formatAddr = (address: string) => formatAddress(address, 8, 6);

onMounted(() => {
  currentTheme.value = userSettings.theme;
  showSparklines.value = userSettings.showSparklines;
  refreshDeletedCircles();
  unsubscribeSettings = userSettings.subscribe(() => {
    currentTheme.value = userSettings.theme;
    showSparklines.value = userSettings.showSparklines;
    refreshDeletedCircles();
  });
});

const toggleChartMode = () => {
  const newMode = props.chartMode === 'gas' ? 'txCount' : 'gas';
  emit('update:chartMode', newMode);
};

const updateTxMultiplier = (event: Event) => {
  const target = event.target as HTMLInputElement;
  emit('update:txMultiplier', parseInt(target.value, 10));
};

const onStartDrag = (e: MouseEvent) => {
  modal.startDrag(e, headerRef.value);
};

const open = () => {
  modal.centerInViewport();
  isOpen.value = true;
  modal.flashHeader();
};

const close = () => {
  isOpen.value = false;
  emit('close');
};

const bringToFront = () => {
  emit('focus');
};

onUnmounted(() => {
  if (unsubscribeSettings) {
    unsubscribeSettings();
  }
});

defineExpose({
  open,
  close,
  setZIndex: modal.setZIndex,
  flashHeader: modal.flashHeader,
  isOpen: () => isOpen.value
});
</script>

<style scoped>
.settings-modal {
  position: fixed;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  font-family: Inter, Arial, sans-serif;
  user-select: none;
  box-shadow: var(--shadow-lg);
  border-radius: 4px;
}

.modal-header {
  height: 40px;
  background: var(--bg-elevated);
  border-bottom: 1px solid var(--border);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 16px;
  cursor: move;
  flex-shrink: 0;
  transition: background 0.3s;
  position: relative;
  border-radius: 4px 4px 0 0;
}

.modal-header.flashing {
  animation: headerFlash 500ms ease-out;
}

@keyframes headerFlash {
  0% { background: var(--accent-primary); opacity: 0.3; }
  100% { background: var(--bg-elevated); }
}

.modal-title {
  color: var(--accent-primary);
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.close-btn {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: 1px solid var(--error);
  color: var(--error);
  width: 24px;
  height: 24px;
  border-radius: 2px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  padding: 0;
}

.close-btn:hover {
  background: var(--error);
  color: var(--text-inverse);
}

.close-icon {
  width: 14px;
  height: 14px;
}

.modal-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  color: var(--text-primary);
  min-height: 0;
}

.setting-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid var(--border);
}

.setting-row:last-of-type {
  border-bottom: none;
}

.hidden-circles-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--border);
}

.section-title {
  font-size: 10px;
  color: var(--text-muted);
  letter-spacing: 2px;
  margin-bottom: 12px;
  text-transform: uppercase;
  font-weight: 600;
}

.setting-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.beta-badge {
  font-size: 9px;
  font-weight: 700;
  color: var(--warning);
  background: rgba(var(--warning-rgb, 255, 193, 7), 0.15);
  border: 1px solid var(--warning);
  padding: 2px 6px;
  border-radius: 3px;
  letter-spacing: 0.5px;
}

.toggle-container {
  display: flex;
  border: 1px solid var(--border);
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;
  border-radius: 4px;
}

.toggle-container:hover {
  border-color: var(--accent-primary);
}

.toggle-container span {
  padding: 8px 14px;
  background: transparent;
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.2s ease;
  border-right: 1px solid var(--border);
}

.toggle-container span:last-child {
  border-right: none;
}

.toggle-container span.active {
  background: var(--accent-primary);
  color: var(--text-inverse);
}

.multiplier-control {
  display: flex;
  align-items: center;
  gap: 8px;
}

.multiplier-control input[type="range"] {
  width: 100px;
  cursor: pointer;
  accent-color: var(--accent-primary);
}

.multiplier-value {
  font-size: 12px;
  color: var(--accent-primary);
  font-weight: 600;
  min-width: 40px;
  text-align: right;
}

.hidden-circles-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
}

.hidden-circle-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  background: var(--hover-overlay);
  border: 1px solid var(--border);
  border-radius: 4px;
}

.circle-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
}

.circle-name {
  font-size: 12px;
  color: var(--text-primary);
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.circle-address {
  font-size: 10px;
  color: var(--text-muted);
  font-family: monospace;
}

.restore-btn {
  background: transparent;
  border: 1px solid var(--success);
  border-radius: 4px;
  width: 24px;
  height: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: all 0.2s;
  flex-shrink: 0;
}

.restore-btn:hover {
  background: var(--success);
}

.restore-icon {
  width: 14px;
  height: 14px;
  color: var(--success);
}

.restore-btn:hover .restore-icon {
  color: var(--text-inverse);
}

.restore-all-btn {
  margin-top: 12px;
  width: 100%;
  padding: 8px;
  background: transparent;
  border: 1px solid var(--success);
  border-radius: 4px;
  color: var(--success);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.2s;
}

.restore-all-btn:hover {
  background: var(--success);
  color: var(--text-inverse);
}

.resize-handle {
  position: absolute;
  background: transparent;
}

.resize-right {
  right: 0;
  top: 0;
  width: 8px;
  height: 100%;
  cursor: ew-resize;
}

.resize-bottom {
  bottom: 0;
  left: 0;
  width: 100%;
  height: 8px;
  cursor: ns-resize;
}

.resize-corner {
  right: 0;
  bottom: 0;
  width: 16px;
  height: 16px;
  cursor: nwse-resize;
  background: var(--accent-primary);
  clip-path: polygon(100% 0, 100% 100%, 0 100%);
}

.resize-corner:hover {
  background: var(--success);
}

.modal-content::-webkit-scrollbar { width: 8px; }
.modal-content::-webkit-scrollbar-track { background: var(--bg-primary); }
.modal-content::-webkit-scrollbar-thumb { background: var(--border); border-radius: 4px; }
.modal-content::-webkit-scrollbar-thumb:hover { background: var(--accent-primary); }
</style>
